/**
 * /settings/branding (Story 14.5 — AC #10)
 *
 * Tenant-level branding configuration: company name, logo, primary/secondary
 * colors, and read-only custom-domain shelf. Lives behind `MANAGE_TENANT`
 * permission (Owner + Admin). Live preview uses the SAME
 * `computePoweredByProvenLeap` helper as the portal so the rendered footer
 * matches what clients see.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
} from "@provenleap/ui";
import { motion } from "framer-motion";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import { usePermission, Actions } from "../../../hooks/usePermission";
import { useAuthStore } from "../../../stores/authStore";
import {
  fetchTenantBranding,
  updateTenantBranding,
  TenantBrandingConflictError,
  type TenantBrandingProjection,
  type TenantBrandingUpdatePatch,
  type TenantBrandingUpdateResult,
} from "../../../lib/tenantBrandingApi";
import { BrandingLogoUpload } from "../../../components/settings/BrandingLogoUpload";
import { PortalBrandingPreview } from "../../../components/settings/PortalBrandingPreview";
import { ContrastBadge } from "../../../components/settings/ContrastBadge";
import { EmailSenderDomainSection } from "../../../components/settings/EmailSenderDomainSection";
import { PortalDomainSection } from "../../../components/settings/PortalDomainSection";
import type { Plan } from "@provenleap/types";
import { expandHexForPicker } from "../../../lib/brandingHelpers";

export const Route = createFileRoute("/_authenticated/settings/branding")({
  component: BrandingSettingsPage,
});

type FormState = {
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  companyName: string | null;
  helpUrl: string | null;
  footerText: string | null;
  fromName: string | null;
};

function diffPatch(
  server: TenantBrandingProjection,
  form: FormState,
  updatedAt: string,
): TenantBrandingUpdatePatch {
  const patch: TenantBrandingUpdatePatch = { updatedAt };
  if ((server.logoUrl ?? null) !== (form.logoUrl ?? null))
    patch.logoUrl = form.logoUrl;
  if ((server.faviconUrl ?? null) !== (form.faviconUrl ?? null))
    patch.faviconUrl = form.faviconUrl;
  if ((server.primaryColor ?? null) !== (form.primaryColor ?? null))
    patch.primaryColor = form.primaryColor;
  if ((server.secondaryColor ?? null) !== (form.secondaryColor ?? null))
    patch.secondaryColor = form.secondaryColor;
  if ((server.companyName ?? null) !== (form.companyName ?? null))
    patch.companyName = form.companyName;
  if ((server.helpUrl ?? null) !== (form.helpUrl ?? null))
    patch.helpUrl = form.helpUrl;
  if ((server.footerText ?? null) !== (form.footerText ?? null))
    patch.footerText = form.footerText;
  if ((server.fromName ?? null) !== (form.fromName ?? null))
    patch.fromName = form.fromName;
  return patch;
}

function isDirty(server: TenantBrandingProjection, form: FormState): boolean {
  return (
    (server.logoUrl ?? null) !== (form.logoUrl ?? null) ||
    (server.faviconUrl ?? null) !== (form.faviconUrl ?? null) ||
    (server.primaryColor ?? null) !== (form.primaryColor ?? null) ||
    (server.secondaryColor ?? null) !== (form.secondaryColor ?? null) ||
    (server.companyName ?? null) !== (form.companyName ?? null) ||
    (server.helpUrl ?? null) !== (form.helpUrl ?? null) ||
    (server.footerText ?? null) !== (form.footerText ?? null) ||
    (server.fromName ?? null) !== (form.fromName ?? null)
  );
}

function BrandingSettingsPage() {
  const { hasPermission } = usePermission();
  const user = useAuthStore((s) => s.user);
  const plan: Plan = (user?.plan as Plan) ?? "free";
  const queryClient = useQueryClient();
  const canManage = hasPermission(Actions.MANAGE_TENANT);

  const [toast, setToast] = useState<{
    type: "success" | "error" | "warning";
    message: string;
    onAction?: { label: string; run: () => void };
  } | null>(null);
  // CR1 L14: track the dismissal timer so we can clear it on unmount and
  // when a new toast replaces the old — prevents "state update on unmounted
  // component" warnings and stale-toast flashes.
  const toastTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current !== null) {
        window.clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  function showToast(
    type: "success" | "error" | "warning",
    message: string,
    onAction?: { label: string; run: () => void },
  ) {
    if (toastTimerRef.current !== null) {
      window.clearTimeout(toastTimerRef.current);
    }
    setToast({ type, message, onAction });
    toastTimerRef.current = window.setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 4000);
  }

  const { data, isLoading, error } = useQuery<TenantBrandingProjection>({
    queryKey: ["tenant", "branding"],
    queryFn: fetchTenantBranding,
    enabled: canManage, // don't fetch at all without permission
  });

  const [form, setForm] = useState<FormState | null>(null);
  // Seed-once-then-never-clobber discipline (CR1 H1): after the first GET
  // hydrates the form, later server updates (triggered by our own mutation's
  // onSuccess) must NOT silently overwrite fields the user is still typing.
  // A manual "Reload branding" (conflict-recovery) path explicitly clears the
  // ref to force a fresh seed.
  const seededRef = useRef(false);

  useEffect(() => {
    if (data && !seededRef.current) {
      setForm({
        logoUrl: data.logoUrl,
        faviconUrl: data.faviconUrl,
        primaryColor: data.primaryColor,
        secondaryColor: data.secondaryColor,
        companyName: data.companyName,
        helpUrl: data.helpUrl,
        footerText: data.footerText,
        fromName: data.fromName,
      });
      seededRef.current = true;
    }
  }, [data]);

  const mutation = useMutation<
    TenantBrandingUpdateResult,
    Error,
    TenantBrandingUpdatePatch
  >({
    mutationFn: updateTenantBranding,
    onSuccess: (result, variables) => {
      queryClient.setQueryData<TenantBrandingProjection>(
        ["tenant", "branding"],
        result.data,
      );
      // CR2 R2-F4: re-sync the form with the authoritative server state so
      // normalization (trim, case, future server-side coercion) is visible and
      // `dirty` returns false after a save. BUT only for the fields THIS patch
      // actually wrote — an atomic logo/favicon confirm must NOT stomp the
      // user's unsaved companyName/color edits (Story 15.2: two atomic-confirm
      // uploaders make this clobber materially more likely). seededRef stays
      // true — we're updating in-place, not seeding.
      const FORM_KEYS: Array<keyof FormState> = [
        "logoUrl",
        "faviconUrl",
        "primaryColor",
        "secondaryColor",
        "companyName",
        "helpUrl",
        "footerText",
        "fromName",
      ];
      const patchedKeys = FORM_KEYS.filter((k) => k in variables);
      setForm((prev) => {
        const next: FormState = prev
          ? { ...prev }
          : {
              logoUrl: result.data.logoUrl,
              faviconUrl: result.data.faviconUrl,
              primaryColor: result.data.primaryColor,
              secondaryColor: result.data.secondaryColor,
              companyName: result.data.companyName,
              helpUrl: result.data.helpUrl,
              footerText: result.data.footerText,
              fromName: result.data.fromName,
            };
        for (const k of patchedKeys) {
          next[k] = result.data[k];
        }
        return next;
      });
      if (result.warnings && result.warnings.length > 0) {
        showToast("warning", result.warnings[0]!);
      } else {
        showToast("success", "Branding updated");
      }
    },
    onError: (err) => {
      if (err instanceof TenantBrandingConflictError) {
        showToast("error", err.message, {
          label: "Reload branding",
          run: () => {
            // Explicit user action: forget the local edits and re-seed from
            // the server. Matches the "Reload" disclosure in the toast copy.
            seededRef.current = false;
            queryClient.invalidateQueries({
              queryKey: ["tenant", "branding"],
            });
          },
        });
        return;
      }
      showToast("error", err.message);
    },
  });

  const dirty = useMemo(() => {
    if (!data || !form) return false;
    return isDirty(data, form);
  }, [data, form]);

  // CR3 R3-F5: warn the user when navigating away with unsaved changes.
  // `seededRef` doesn't survive unmount/remount, so a bare navigation to
  // another settings page + back would clobber the draft. The beforeunload
  // guard catches tab-close + full-page reloads; SPA navigation intent is
  // out-of-scope here but the browser's native confirm covers the
  // high-stakes paths (tab close, refresh, manual URL change).
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Permission gate at render time (defense-in-depth; server returns 403 and
  // the settings-index card is already hidden).
  if (!canManage) {
    return (
      <div className="p-6 max-w-[1200px] mx-auto">
        <ReportBreadcrumb
          items={[{ label: "Settings", to: "/settings" }, { label: "Branding" }]}
        />
        <Card className="!rounded-[22px] border-[0.5px] border-[var(--hairline)] shadow-xs mt-4">
          <CardContent className="py-8 text-center text-muted-foreground">
            You don&apos;t have permission to manage branding.
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading || !form) {
    return (
      <div className="p-6 max-w-[1200px] mx-auto">
        <ReportBreadcrumb
          items={[{ label: "Settings", to: "/settings" }, { label: "Branding" }]}
        />
        <div className="mt-4 text-[13px] text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-[1200px] mx-auto">
        <ReportBreadcrumb
          items={[{ label: "Settings", to: "/settings" }, { label: "Branding" }]}
        />
        <div className="mt-4 text-[13px] text-red-700">
          Failed to load branding configuration.
        </div>
      </div>
    );
  }

  function handleLogoUpload(newUrl: string | null) {
    setForm((prev) => (prev ? { ...prev, logoUrl: newUrl } : prev));
    // Immediately persist via PATCH so the uploaded R2 object is "confirmed"
    // before the user navigates away (atomic with OCC updatedAt).
    // CR1 H2: send ONLY logoUrl so we never carry uncommitted edits from other
    // fields. This keeps logo confirmation atomic without coupling unrelated
    // form state.
    // CR2 R2-F5: block a second PATCH while one is in flight — the two would
    // share the same (stale) updatedAt and the loser would 409. The Logo
    // uploader is also `disabled` during isPending, but this is a belt-and-
    // braces guard against rapid programmatic triggers.
    if (!data || mutation.isPending) return;
    mutation.mutate({ logoUrl: newUrl, updatedAt: data.updatedAt });
  }

  function handleFaviconUpload(newUrl: string | null) {
    setForm((prev) => (prev ? { ...prev, faviconUrl: newUrl } : prev));
    // Same atomic-confirm pattern as the logo: persist ONLY faviconUrl with the
    // current OCC updatedAt; block while a PATCH is in flight (shared stale
    // updatedAt would 409).
    if (!data || mutation.isPending) return;
    mutation.mutate({ faviconUrl: newUrl, updatedAt: data.updatedAt });
  }

  function handleSave() {
    if (!data) return;
    mutation.mutate(diffPatch(data, form!, data.updatedAt));
  }

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      <ReportBreadcrumb
        items={[{ label: "Settings", to: "/settings" }, { label: "Branding" }]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
        {/* Form column */}
        <div className="lg:col-span-2 space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.05 }}
          >
            <Card className="!rounded-[22px] border-[0.5px] border-[var(--hairline)] shadow-xs">
              <CardHeader>
                <CardTitle>Identity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName">Company name</Label>
                  <Input
                    id="companyName"
                    value={form.companyName ?? ""}
                    onChange={(e) =>
                      setForm((prev) =>
                        prev
                          ? {
                              ...prev,
                              companyName:
                                e.target.value.trim().length > 0
                                  ? e.target.value.slice(0, 80)
                                  : null,
                            }
                          : prev,
                      )
                    }
                    maxLength={80}
                    // CR1 L12: don't leak email as placeholder — branding is
                    // tenant-scoped; a support screenshot would otherwise
                    // expose the current admin's email.
                    placeholder="Your agency name"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Leave blank to fall back to your workspace name.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="branding-logo">Logo</Label>
                  <BrandingLogoUpload
                    currentUrl={form.logoUrl}
                    onUploadComplete={handleLogoUpload}
                    onError={(msg) => showToast("error", msg)}
                    disabled={mutation.isPending}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    This logo appears on all client portals you share.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="branding-favicon">Favicon</Label>
                  <BrandingLogoUpload
                    asset="favicon"
                    currentUrl={form.faviconUrl}
                    onUploadComplete={handleFaviconUpload}
                    onError={(msg) => showToast("error", msg)}
                    disabled={mutation.isPending}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Shown in the browser tab on your client portals.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="branding-help-url">Help link URL</Label>
                  <Input
                    id="branding-help-url"
                    type="url"
                    inputMode="url"
                    placeholder="https://help.youragency.com"
                    value={form.helpUrl ?? ""}
                    onChange={(e) =>
                      setForm((prev) =>
                        prev
                          ? {
                              ...prev,
                              helpUrl:
                                e.target.value.trim().length > 0
                                  ? e.target.value.trim()
                                  : null,
                            }
                          : prev,
                      )
                    }
                  />
                  {form.helpUrl && !form.helpUrl.startsWith("https://") ? (
                    <p className="text-[11px] text-destructive">
                      Help link must start with https://
                    </p>
                  ) : (
                    <p className="text-[11px] text-muted-foreground">
                      Shown as a “Help” link on your client portals. Must start
                      with https://
                    </p>
                  )}
                </div>

                {/* Story 7.10 — white-label footer text (PDF + email footer). */}
                <div className="space-y-2">
                  <Label htmlFor="branding-footer-text">Footer text</Label>
                  <Input
                    id="branding-footer-text"
                    placeholder="© 2026 Your Agency · 555 Main St, Springfield"
                    value={form.footerText ?? ""}
                    maxLength={200}
                    onChange={(e) =>
                      setForm((prev) =>
                        prev
                          ? {
                              ...prev,
                              footerText:
                                e.target.value.trim().length > 0
                                  ? e.target.value.slice(0, 200)
                                  : null,
                            }
                          : prev,
                      )
                    }
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Shown in the footer of report PDFs and delivery emails.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.1 }}
          >
            <Card className="!rounded-[22px] border-[0.5px] border-[var(--hairline)] shadow-xs">
              <CardHeader>
                <CardTitle>Colors</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ColorField
                  id="primaryColor"
                  label="Primary color"
                  value={form.primaryColor}
                  onChange={(next) =>
                    setForm((prev) =>
                      prev ? { ...prev, primaryColor: next } : prev,
                    )
                  }
                />
                <ColorField
                  id="secondaryColor"
                  label="Secondary color"
                  helperText="Used for borders on your client portals."
                  value={form.secondaryColor}
                  onChange={(next) =>
                    setForm((prev) =>
                      prev ? { ...prev, secondaryColor: next } : prev,
                    )
                  }
                />
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.15 }}
          >
            <Card className="!rounded-[22px] border-[0.5px] border-[var(--hairline)] shadow-xs">
              <CardHeader>
                <CardTitle>Domain</CardTitle>
              </CardHeader>
              <CardContent>
                <PortalDomainSection plan={plan} />
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.2 }}
          >
            <Card className="!rounded-[22px] border-[0.5px] border-[var(--hairline)] shadow-xs">
              <CardHeader>
                <CardTitle>Email</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Story 7.10 — sender display name for the report-delivery From. */}
                <div className="space-y-2">
                  <Label htmlFor="branding-from-name">Sender name</Label>
                  <Input
                    id="branding-from-name"
                    placeholder="Your Agency Reports"
                    value={form.fromName ?? ""}
                    maxLength={80}
                    onChange={(e) =>
                      setForm((prev) =>
                        prev
                          ? {
                              ...prev,
                              fromName:
                                e.target.value.trim().length > 0
                                  ? e.target.value.slice(0, 80)
                                  : null,
                            }
                          : prev,
                      )
                    }
                  />
                  <p className="text-[11px] text-muted-foreground">
                    The display name shown on report delivery emails (the From
                    address is unchanged).
                  </p>
                </div>
                <EmailSenderDomainSection plan={plan} />
              </CardContent>
            </Card>
          </motion.div>

          <div className="flex items-center justify-between">
            <p className="text-[12px] text-muted-foreground">
              {dirty ? "Unsaved changes" : "All changes saved"}
            </p>
            <Button
              onClick={handleSave}
              disabled={!dirty || mutation.isPending}
              aria-describedby={toast ? "branding-toast" : undefined}
            >
              {mutation.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>

        {/* Preview column */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-3">
            <PortalBrandingPreview
              branding={{
                logoUrl: form.logoUrl,
                primaryColor: form.primaryColor,
                secondaryColor: form.secondaryColor,
                companyName: form.companyName,
                helpUrl: form.helpUrl,
              }}
              plan={plan}
            />
          </div>
        </div>
      </div>

      {toast ? (
        <div
          id="branding-toast"
          role="status"
          aria-live="polite"
          className={`fixed bottom-4 right-4 z-50 rounded-2xl border-[0.5px] shadow-xs px-4 py-3 text-[13px] bg-card ${
            toast.type === "error"
              ? "border-red-300 text-red-900"
              : toast.type === "warning"
              ? "border-amber-300 text-amber-900"
              : "border-emerald-300 text-emerald-900"
          }`}
        >
          <div className="flex items-center gap-3">
            <span>{toast.message}</span>
            {toast.onAction ? (
              <button
                type="button"
                onClick={toast.onAction.run}
                className="underline underline-offset-2 text-[12px]"
              >
                {toast.onAction.label}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ColorField({
  id,
  label,
  value,
  onChange,
  helperText,
}: {
  id: string;
  label: string;
  value: string | null;
  onChange: (next: string | null) => void;
  helperText?: string;
}) {
  const normalized = value ?? "";
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <Label htmlFor={id}>{label}</Label>
        {helperText ? (
          <span className="text-[11px] text-muted-foreground">{helperText}</span>
        ) : null}
      </div>
      <div className="flex items-center gap-3">
        <Input
          id={id}
          value={normalized}
          maxLength={7}
          onChange={(e) => {
            const next = e.target.value.trim();
            onChange(next.length > 0 ? next : null);
          }}
          placeholder="#0ea5e9"
        />
        <input
          type="color"
          aria-label={`${label} color picker`}
          value={expandHexForPicker(normalized)}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 w-11 rounded-md border-[0.5px] border-[var(--hairline)] cursor-pointer"
        />
      </div>
      {value ? (
        <div className="flex gap-2">
          <ContrastBadge
            colorHex={value}
            background="white"
            label="Against white background"
          />
          <ContrastBadge
            colorHex={value}
            background="deep"
            label="Against deep-gray text"
          />
        </div>
      ) : null}
    </div>
  );
}

